import { z } from 'zod';

import { UuidSchema } from '../../common/id/id';
import { BillingCurrencySchema } from '../currency/currency';
import { MoneyAmountSchema } from '../money/money';
import { ProductKeySchema } from '../product-key/product-key';
import { ProductKindSchema } from '../product-kind/product-kind';

/**
 * "What would this member pay for this plan?" — asked before anything is
 * written.
 *
 * It exists so the order-entry form can show the real numbers rather than making
 * a staff member create a draft to discover them, and so the two never disagree:
 * the quote and the draft are produced by the same code path.
 */
export const DirectPaymentQuoteQuerySchema = z
  .object({
    userId: UuidSchema,
    productKey: ProductKeySchema,
  })
  .strict();

export type DirectPaymentQuoteQuery = z.infer<typeof DirectPaymentQuoteQuerySchema>;

/**
 * The resolved terms, and **nothing is claimed by asking**.
 *
 * The same words `CoursesGetActiveDiscountQuery` uses about itself, and they
 * matter for the same reason: a grant may expire, be revoked, or be spent by
 * another order between this answer and the submit that acts on it. The submit
 * re-resolves and claims; this is what a staff member is shown, not a promise.
 *
 * `grantIds` is here so the surface can say *which* earned discounts are being
 * applied, not so a client can send them back — the submit ignores anything a
 * caller supplies and asks again.
 */
export const DirectPaymentQuoteSchema = z
  .object({
    productKey: ProductKeySchema,
    kind: ProductKindSchema,
    /** The plan's own duration, so the form can say what the member is buying; `null` for a fee. */
    durationDays: z.number().int().positive().nullable(),
    /** Resolved in the member's gender scope (U15). */
    listAmount: MoneyAmountSchema,
    discountAmount: MoneyAmountSchema,
    chargedAmount: MoneyAmountSchema,
    currency: BillingCurrencySchema,
    grantIds: z.array(UuidSchema),
  })
  .strict();

export type DirectPaymentQuote = z.infer<typeof DirectPaymentQuoteSchema>;
